import { useEffect, useRef, useState } from "react";

// Double-click (or an external autoStart signal) to edit a name in place. Enter/blur
// commits, Esc cancels. Used for field + folder renaming (Front-End.md §A9/§A10).
export function InlineEdit({
  value,
  onCommit,
  autoStart = false,
  onDone,
  className = "",
  inputClassName = "",
  title = "Double-click to rename",
}: {
  value: string;
  onCommit: (next: string) => void;
  autoStart?: boolean;
  onDone?: () => void;
  className?: string;
  inputClassName?: string;
  title?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoStart) {
      setDraft(value);
      setEditing(true);
    }
  }, [autoStart, value]);

  useEffect(() => {
    if (editing) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [editing]);

  const finish = (commit: boolean) => {
    setEditing(false);
    onDone?.();
    const next = draft.trim();
    if (commit && next && next !== value) onCommit(next);
  };

  if (!editing) {
    return (
      <span
        className={`cursor-text ${className}`}
        title={title}
        onDoubleClick={(e) => {
          e.stopPropagation();
          setDraft(value);
          setEditing(true);
        }}
      >
        {value}
      </span>
    );
  }

  return (
    <input
      ref={ref}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => finish(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter") finish(true);
        else if (e.key === "Escape") finish(false);
      }}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      draggable={false}
      className={`rounded border border-line bg-canvas px-1.5 py-0.5 text-sm text-fg outline-none focus:border-muted ${inputClassName}`}
    />
  );
}
